import { cn } from '@crowdex/ui/lib/utils';
import { LayoutGrid, LibraryBig } from 'lucide-react';
import {
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  useCallback,
  useRef,
  useState,
} from 'react';
import { ItemIcon } from '@/components/item-icon';
import { useLibraryPage, useRouter } from '@/lib/navigation';
import { setAllAppsOpen, useAllAppsOpen } from './all-apps';
import {
  consumeDragClick,
  type FavoriteSlot,
  FavoritesMenu,
  NO_WINDOW_DRAG,
  useFavoriteDragSource,
  useFavoritesZone,
} from './favorite-drag';
import { type FavoriteItem, useAppOnScreen, useFavorites } from './favorites';

/** The narrowest a sidebar tile gets (the charter's mouse target), and the gap between two. */
const MIN_TILE = 32;
const TILE_GAP = 6;

/**
 * How many tiles of at least `MIN_TILE` px a row `width` px wide holds; null until measured
 * (no limit then).
 */
export function tilesThatFit(width: number): number | null {
  if (!(width > 0)) return null;
  return Math.max(2, Math.floor((width + TILE_GAP) / (MIN_TILE + TILE_GAP)));
}

/**
 * The sidebar's row: as many columns as tiles (at least the tiles per row the user chose: they
 * size the tiles when there are few), as many as fit; those that do not fit wait behind All apps.
 */
export function sidebarRow(
  tiles: number,
  perRow: number,
  capacity: number | null,
): { columns: number; shown: number } {
  // All apps is always last, always shown.
  const total = tiles + 1;
  if (capacity === null || total <= capacity)
    return { columns: Math.max(perRow, total), shown: tiles };
  return { columns: capacity, shown: capacity - 1 };
}

/** The width of the element `ref` gets, followed as it resizes. */
function useWidth() {
  const [width, setWidth] = useState(0);
  const observer = useRef<ResizeObserver | null>(null);
  const ref = useCallback((node: HTMLElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (node === null) return;
    setWidth(node.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return;
    observer.current = new ResizeObserver(([entry]) => {
      if (entry !== undefined) setWidth(entry.contentRect.width);
    });
    observer.current.observe(node);
  }, []);
  return { width, ref };
}

/**
 * The favourites: the tiles the user laid out, then All apps, always last. Icon only on the
 * sidebar (the name in the tooltip), on one row: the tiles narrow to fit it, and those that would
 * not fit at all wait behind All apps, which counts them; the drawer (mobile 2) shows them with
 * their names, a row of the tiles per row after another. They change in place (see
 * `favorite-drag`): drag a tile to move it, out of the tiles to unpin it; right-click (or
 * long-press and let go) for Move, Unpin and the tiles per row.
 */
export function FavoriteTiles({
  density = 'desktop',
  onLeave = (then) => then(),
}: {
  density?: 'desktop' | 'drawer';
  /** What a tile opens over the drawer closes it first (the phone's history). */
  onLeave?: (then: () => void) => void;
}) {
  const router = useRouter();
  const { pathname, leave } = useLibraryPage();
  const allAppsOpen = useAllAppsOpen();
  const shownApp = useAppOnScreen();
  const favorites = useFavorites();
  const perRowDensity = density === 'drawer' ? 'phone' : 'desktop';
  const chosen = favorites.columns[perRowDensity];
  const measured = useWidth();
  // The tiles of the row (the dragged one away from them does not count).
  const placed = favorites.items.length;
  const row =
    density === 'desktop'
      ? sidebarRow(placed, chosen, tilesThatFit(measured.width))
      : { columns: chosen, shown: placed };
  const perRow = row.columns;
  const zone = useFavoritesZone(favorites, { layout: 'grid', perRow });
  const drag = useFavoriteDragSource(favorites);
  const hidden = Math.max(0, placed - row.shown);
  // One ref for the drop zone and the measure, the same from one render to the next.
  const zoneRef = useRef(zone.ref);
  zoneRef.current = zone.ref;
  const measure = measured.ref;
  const gridRef = useCallback(
    (node: HTMLElement | null) => {
      zoneRef.current(node);
      measure(node);
    },
    [measure],
  );
  // The tiles shown: the first ones; the dragged one keeps its place among them.
  let tiles = 0;
  const slots = zone.slots.filter((slot) => {
    if (slot.state === 'away' || slot.item === null || slot.state === 'placeholder') return true;
    tiles += 1;
    return tiles <= row.shown;
  });

  const icon = (item: FavoriteItem) =>
    item.kind === 'library' ? (
      <LibraryBig className="size-[18px]" />
    ) : (
      <ItemIcon className="size-[18px]" icon={item.app.icon} src={item.app.iconSrc} />
    );
  const open = (item: FavoriteItem) =>
    onLeave(() => {
      setAllAppsOpen(false);
      if (item.kind === 'library') router.push('/artifacts');
      else favorites.openApp(item.app.view);
    });
  const active = (item: FavoriteItem) =>
    !allAppsOpen &&
    (item.kind === 'library' ? pathname === '/artifacts' : shownApp === item.app.view);

  return (
    <>
      <FavoritesMenu density={perRowDensity} favorites={favorites} layout="grid" zone={zone}>
        <nav
          aria-label="Favourites"
          className={cn('grid', density === 'desktop' ? 'gap-1.5' : 'gap-2')}
          // Electron never moves the window from the tiles: they drag themselves.
          data-electron-no-drag-region
          data-favorites-over={zone.over || undefined}
          ref={gridRef}
          style={{ ...NO_WINDOW_DRAG, gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))` }}
        >
          {slots.map(({ entry, item, state }) =>
            item === null ? (
              // The place an app dragged from All apps would take.
              <span
                aria-hidden
                className={cn(
                  'border border-dashed border-hairline-strong',
                  density === 'desktop' ? 'h-9 rounded-xl' : 'h-16 rounded-2xl',
                )}
                data-favorite-placeholder
                data-favorite-slot={entry}
                key={entry}
              />
            ) : (
              <Tile
                active={active(item)}
                density={density}
                entry={entry}
                held={drag.held === entry}
                key={entry}
                label={item.title}
                onKeyDown={(event) => zone.onKeyDown(event, item)}
                onPointerDown={(event) =>
                  drag.begin(event, {
                    entry,
                    title: item.title,
                    icon: icon(item),
                    from: 'favorites',
                  })
                }
                onSelect={() => open(item)}
                state={state}
              >
                {icon(item)}
              </Tile>
            ),
          )}
          <Tile
            active={allAppsOpen}
            badge={hidden > 0 ? `+${hidden}` : undefined}
            dashed={density === 'drawer'}
            density={density}
            label={
              density === 'drawer'
                ? 'All'
                : hidden > 0
                  ? `All apps (${hidden} more ${hidden === 1 ? 'favourite' : 'favourites'})`
                  : 'All apps'
            }
            onSelect={() => {
              leave();
              setAllAppsOpen(!allAppsOpen);
            }}
          >
            <LayoutGrid className="size-[18px]" />
          </Tile>
        </nav>
      </FavoritesMenu>
      {zone.announcement}
      {drag.ghost}
    </>
  );
}

function Tile({
  label,
  badge,
  entry,
  active = false,
  dashed = false,
  held = false,
  state = 'tile',
  density,
  onSelect,
  onPointerDown,
  onKeyDown,
  children,
}: {
  label: string;
  /** What it counts (All apps: the favourites that do not fit the row). */
  badge?: string | undefined;
  /** A favourite's entry; none for All apps, which neither moves nor goes. */
  entry?: string;
  active?: boolean;
  dashed?: boolean;
  /** A finger lifted it (its long press): it drags as it moves, or opens the menu. */
  held?: boolean;
  /** Dragged: its place among the tiles (an empty slot), or hidden once out of them. */
  state?: FavoriteSlot['state'];
  density: 'desktop' | 'drawer';
  onSelect: () => void;
  onPointerDown?: (event: PointerEvent<HTMLButtonElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
  children: ReactNode;
}) {
  return (
    <button
      aria-current={active ? 'page' : undefined}
      aria-keyshortcuts={entry === undefined ? undefined : 'Alt+ArrowLeft Alt+ArrowRight'}
      aria-label={density === 'desktop' ? label : undefined}
      className={cn(
        'relative flex min-w-0 select-none items-center justify-center text-muted-foreground transition-colors [-webkit-touch-callout:none] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        density === 'desktop'
          ? 'h-9 rounded-xl bg-control/45 hover:bg-control/80'
          : 'h-16 flex-col gap-1 rounded-2xl bg-control/45 px-1 text-foreground/85',
        dashed && 'border border-dashed border-hairline-strong bg-transparent',
        active && 'bg-control text-foreground',
        held && 'bg-control shadow-md',
        // The dragged tile: the ghost carries it; its place stays, empty, among the others.
        state === 'placeholder' &&
          'border border-dashed border-hairline-strong bg-transparent shadow-none hover:bg-transparent [&>*]:invisible',
        state === 'away' && 'hidden',
      )}
      data-favorite-fixed={entry === undefined || undefined}
      data-favorite-placeholder={state === 'placeholder' || undefined}
      data-favorite-slot={state === 'away' ? undefined : entry}
      onClick={() => {
        if (consumeDragClick()) return;
        onSelect();
      }}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      title={label}
      type="button"
    >
      {children}
      {badge !== undefined && (
        <span
          aria-hidden
          className="type-caption absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-control px-1 text-foreground tabular-nums"
          data-favorite-more
        >
          {badge}
        </span>
      )}
      {density === 'drawer' && <span className="type-caption max-w-full truncate">{label}</span>}
    </button>
  );
}
